import { USER_TYPES, UserTypeCode } from "api/api.typing";
import EmptyState from "components/EmptyState";
import Loader from "components/Loader";
import NavigationTabsHeader from "components/header/NavigationTabsHeader";
import TaskBaseListFilter from "pages/project/common/taskBase/filter/TaskBaseListFilter";
import { useTaskBaseFilter } from "pages/project/common/taskBase/filter/useTaskBaseFilter";
import TaskBaseListCard from "pages/project/common/taskBase/list/TaskBaseListCard";
import DeliverTaskModal from "pages/project/task/components/modals/DeliverTaskModal";
import FeedbackModal from "pages/project/task/components/modals/FeedbackModal";
import {
  MASTER_DETAIL_SIDE_BY_SIDE_MEDIA_QUERY,
  masterDetailMasterClassName,
  masterDetailRowClassName,
} from "pages/project/common/masterDetailLayout";
import TaskSideBar from "pages/project/task/components/sidebar/TaskSideBar";
import { useTaskCounts, useTaskListByType } from "query/task.query";
import { useUser } from "query/user.query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { TASK_LIST_TYPE, TaskListRouteParam, TaskListType } from "./task.typing";
import { TaskListTable } from "./components/TaskListTable";
import { GroupedTaskListTable } from "./components/GroupedTaskListTable";
import { useTempoChat } from "hooks/useTempoChat";
import TempoChatModal from "components/TempoChat/TempoChatModal";
import { cn } from "utils/ui";

const partialTabs: Array<{ name: string; to: TaskListType; count: number; forUserTypes: UserTypeCode[] }> = [
  {
    name: "task.navigation.upcoming",
    to: TASK_LIST_TYPE.upcoming,
    count: 0,
    forUserTypes: [USER_TYPES.supplier, USER_TYPES.customer],
  },
  {
    name: "task.navigation.ongoing",
    to: TASK_LIST_TYPE.ongoing,
    count: 0,
    forUserTypes: [USER_TYPES.supplier, USER_TYPES.customer],
  },
  {
    name: "task.navigation.delivered",
    to: TASK_LIST_TYPE.delivered,
    count: 0,
    forUserTypes: [USER_TYPES.supplier],
  },
  {
    name: "task.navigation.validated",
    to: TASK_LIST_TYPE.validated,
    count: 0,
    forUserTypes: [USER_TYPES.customer],
  },
  { name: "task.navigation.completed", to: TASK_LIST_TYPE.completed, count: 0, forUserTypes: [USER_TYPES.supplier] },
];

function TaskListPage() {
  const { t } = useTranslation();
  const [filterParams, onChangeParams] = useTaskBaseFilter("task");
  const { id: taskId, type = TASK_LIST_TYPE.upcoming, action } = useParams<TaskListRouteParam>();
  const { tasks, hasTasks, isLoading, error } = useTaskListByType(type, filterParams);
  const { tempoChatData, handleChatClose } = useTempoChat();

  const counts = useTaskCounts();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const { data: currentUser } = useUser();

  useEffect(() => {
    // Below lg the panel replaces the list, but the window keeps the list's scroll offset.
    if (taskId && !window.matchMedia(MASTER_DETAIL_SIDE_BY_SIDE_MEDIA_QUERY).matches) {
      window.scrollTo({ top: 0 });
    }
  }, [taskId]);

  if (!currentUser) {
    return null;
  }

  const isTaskDetailOpen = !!taskId;
  const isUserSupplier = currentUser.userType === USER_TYPES.supplier;
  const taskBaseStartDateLabel =
    type === TASK_LIST_TYPE.upcoming ? t("task-base.fields.estimatedStartOn") : t("task-base.fields.startOn");
  const tabs = partialTabs
    .filter((tab) => tab.forUserTypes.includes(currentUser.userType))
    .map((tab) => ({
      ...tab,
      count: counts[tab.to] ?? 0,
      buildUrl: () => `/project/task/list/${tab.to}?${searchParams.toString()}`,
    }));

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col">
          <NavigationTabsHeader
            tabs={tabs}
            selectedTab={type}
            dataTestId="tasks-nav"
            pageName="tasks"
            subTitle={t(`task.subTitle.${type}`)}
            title={t("task.title")}
          />
          <div className={cn(masterDetailRowClassName, "min-h-0 flex-1")}>
            <div data-test="task-list-master" className={masterDetailMasterClassName(isTaskDetailOpen, "column")}>
              <TaskBaseListFilter
                isDetailOpen={isTaskDetailOpen}
                params={filterParams}
                onChange={(newParams) => {
                  onChangeParams(newParams);
                  // When enabling grouping and view is not table, switch to table
                  if (newParams.isGrouped && newParams.view !== "table") {
                    onChangeParams({ ...newParams, view: "table" });
                  }
                }}
                isUserSupplier={isUserSupplier}
              />
              <div data-test="task-list-scroll" className="lg:min-h-0 lg:flex-1 lg:overflow-auto">
                <Loader isShown={isLoading} />
                {!isLoading && !hasTasks && (
                  <EmptyState
                    title={t(`task.empty.${type}.title`)}
                    subtitle={
                      error
                        ? t("error:common.server")
                        : filterParams.term && filterParams.term.length > 0
                          ? t("task.empty.noResult")
                          : t(`task.empty.${type}.subtitle`)
                    }
                  />
                )}
                {!isLoading && hasTasks && (
                  <>
                    <div className="block lg:hidden">
                      <TaskBaseListCard
                        type="task"
                        selectedTaskBaseId={taskId}
                        tasksBase={tasks}
                        listFilterParams={filterParams}
                        taskBaseStartDateLabel={taskBaseStartDateLabel}
                        isUserSupplier={isUserSupplier}
                        currentListType={type}
                      />
                    </div>
                    <div className="hidden lg:block">
                      {filterParams.view === "table" ? (
                        filterParams.isGrouped ? (
                          <GroupedTaskListTable
                            tasks={tasks}
                            shownDetailDataId={taskId}
                            filterParams={filterParams}
                            isUserSupplier={isUserSupplier}
                            taskListType={type}
                          />
                        ) : (
                          <TaskListTable
                            tasks={tasks}
                            shownDetailDataId={taskId}
                            filterParams={filterParams}
                            isUserSupplier={isUserSupplier}
                            taskListType={type}
                          />
                        )
                      ) : (
                        <TaskBaseListCard
                          type="task"
                          selectedTaskBaseId={taskId}
                          tasksBase={tasks}
                          listFilterParams={filterParams}
                          taskBaseStartDateLabel={taskBaseStartDateLabel}
                          isUserSupplier={isUserSupplier}
                          currentListType={type}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            {!isLoading && hasTasks && (
              <>
                <TaskSideBar
                  selectedTaskId={taskId}
                  onClose={() => navigate(`/project/task/list/${type}?${searchParams.toString()}`)}
                  onDeliverClicked={() =>
                    navigate(`/project/task/list/${type}/${taskId}/deliver?${searchParams.toString()}`)
                  }
                  onErrorNode={() => navigate(`/project/task/list/${type}?${filterParams.toString()}`)}
                  filterType={type}
                />
                {taskId && action === "deliver" && (
                  <DeliverTaskModal taskId={taskId} show={true} isUserSupplier={isUserSupplier} />
                )}
                {taskId && action === "feedback" && (
                  <FeedbackModal
                    taskId={taskId}
                    show={true}
                    onClose={() => navigate(`/project/task/list/${type}/${taskId}?${searchParams.toString()}`)}
                  />
                )}
              </>
            )}
            {tempoChatData && (
              <TempoChatModal show={!!tempoChatData} tempoChatData={tempoChatData} onClose={handleChatClose} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default TaskListPage;
